/**
 * Client for the pages of a brand's site that show game lists, as spark-creatives reads them from the site's
 * templatization config (`/creatives/casino-instance/{id}/site-pages`).
 *
 * A brand's site is not two pages. The tab used to assume a home page and a "/casino"; dev-iulian has eight
 * pages with game lists and shows no "Casino" in its navigation. Two kinds, from what decides their lists:
 *
 * - `sections`: the page reads a page-sections source, whose items are its sections. The page's arrangement
 *   (order, Hide, the owner's own lists) works on it when `overrides_allowed`.
 * - `category`: the page draws one game carousel per link of a navigation list. Its games can be reordered
 *   list by list; arranging its lists is not built yet.
 * - `list`: a single-list page served by a games-lister route (`/slots/:sourceId`, `/live-casino/:sourceId`),
 *   named by `source_id`. Its games can be reordered; it sits under the section page named by `parent`. Its route can
 *   also show a page of sections under the list (`page_source_id`), which is arranged as a `sections` page's source is.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from './creative-api';
import { tenantSourceResult, type TenantSourceResult } from './creatives-tenant-source';

export type SitePageKind = 'sections' | 'category' | 'list';

/** A section of a `sections` page that shows a list, as the service reads the page the way the site does (the data
 *  API's resolve with the site's guest token, so with the brand's override applied). */
export interface SitePageList {
  /** The list's data-source id. */
  listId: string;
  /** The title the section shows above the list, per language (one that is not per language is under `en`); empty
   *  when the section shows none. */
  title: Record<string, string>;
  /** The brand's renderer hides the section; the site still fetches its list. */
  inactive: boolean;
  /** `mobile` or `desktop`: the section shows on that device only. '' otherwise. */
  mode: string;
  /** The list's element type as the site resolves it (`slot_game` for a game list); '' when the service does not say. */
  listType: string;
}

export interface SitePage {
  path: string;
  kind: SitePageKind;
  /** The source that decides which lists the page shows, and in what order. */
  source_id: string;
  /** The service may override that source for the brand: a `sections` or `category` page's source, which arranging the
   *  page needs, and on a `list` page the list's own source. The lists under a list page's list are arranged through
   *  `pageSourceId` instead. */
  overrides_allowed: boolean;
  /** What the site's own navigation calls the page, per language (`{ en: 'Slots', es: 'Tragamonedas' }`).
   *  Absent when no navigation link names it. */
  label?: Record<string, string>;
  /** For a `list` page, the path of the section page it belongs under. */
  parent?: string;
  /** For a `list` page, whether the menu of the page it belongs under links it. Its path can place it under a page whose
   *  menu does not (playavenue's "Otro" sits under Casino en Vivo, and only a hidden page's menu links it): such a list
   *  is reached only if the site's navigation links it. Absent when the service does not say. */
  inMenu?: boolean;
  /** The market of the lists the page shows (`MEX`): the region of the override record that put its source on the site,
   *  directly or through the navigation that links it. Absent when its lists are no market's. */
  market?: string;
  /** Whether a navigation the site renders links the page: on any page's layout or template, of any of the four
   *  navigation types, a link at any depth, the category pages' own menus excepted. Read as the live site resolves its
   *  navigation (the brand's overrides applied), and it counts only when the answer's `linkedKnown` holds. Absent when
   *  the service does not say. */
  linked?: boolean;
  /** What that navigation calls the page, per language, from the first top-level link to it with a text. Absent when no
   *  such link has one, or the service does not say. */
  navLabel?: Record<string, string>;
  /** For a `sections` page, each of its sections that shows a list, in the order the site renders them (a list two
   *  sections show is there twice). Absent when the service did not read the page that way (another kind of page, a
   *  site whose page names no tenant, a read that failed, or a service from before `lists`); empty when the page shows
   *  no list. */
  lists?: SitePageList[];
  /** For a `list` page, the page of sections its route shows under the list (after the list and its providers), when
   *  the brand may arrange it through the page-sections routes, as a `sections` page's source. The platform may not
   *  have made it yet: those routes then read it as an empty page given the site's domain, and its first publish makes
   *  it. Absent on another kind of page, on a list page whose route shows none, and when the service could not read
   *  that page (a degraded answer, see `parseDegraded`). */
  pageSourceId?: string;
}

/** Whether the owner can arrange a page's lists (their order, Hide, their own lists): a `sections` or `category` page
 *  whose source allows the brand's override, or a `list` page with a page of sections under its list (the lists under
 *  it). Game Lists offers that on the page, and FTUX's list piece reads it to know whether Add list is there. */
export const pageArrangeable = (page: Pick<SitePage, 'kind' | 'overrides_allowed' | 'pageSourceId'>): boolean =>
  ((page.kind === 'sections' || page.kind === 'category') && page.overrides_allowed) ||
  (page.kind === 'list' && (page.pageSourceId ?? '') !== '');

const isKind = (value: unknown): value is SitePageKind =>
  value === 'sections' || value === 'category' || value === 'list';

/** A `{ language: text }` map, keeping only the non-empty texts; null when none is left. */
const labelMap = (value: unknown): Record<string, string> | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const entries = Object.entries(value).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim() !== '',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

/** A page's lists, keeping the rows that name one. */
const parseSitePageLists = (rows: unknown[]): SitePageList[] =>
  rows
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .filter((row) => typeof row.list_id === 'string' && row.list_id.trim() !== '')
    .map((row) => ({
      listId: row.list_id as string,
      title: labelMap(row.title) ?? {},
      inactive: row.inactive === true,
      mode: typeof row.mode === 'string' ? row.mode : '',
      listType: typeof row.list_type === 'string' ? row.list_type : '',
    }));

/** Parse a site-pages body, keeping the rows that name a path and a kind this hub knows. */
export const parseSitePages = (body: Record<string, unknown>): SitePage[] =>
  (Array.isArray(body.pages) ? body.pages : [])
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .filter((row) => typeof row.path === 'string' && row.path.startsWith('/') && isKind(row.kind))
    .map((row) => {
      const page: SitePage = {
        path: row.path as string,
        kind: row.kind as SitePageKind,
        source_id: typeof row.source_id === 'string' ? row.source_id : '',
        overrides_allowed: row.overrides_allowed === true,
      };
      const label = labelMap(row.label);
      if (label) page.label = label;
      if (typeof row.parent === 'string' && row.parent.startsWith('/')) page.parent = row.parent;
      if (typeof row.in_menu === 'boolean') page.inMenu = row.in_menu;
      if (typeof row.market === 'string' && row.market.trim()) page.market = row.market.trim().toUpperCase();
      if (typeof row.linked === 'boolean') page.linked = row.linked;
      const navLabel = labelMap(row.nav_label);
      if (navLabel) page.navLabel = navLabel;
      if (Array.isArray(row.lists)) page.lists = parseSitePageLists(row.lists);
      if (page.kind === 'list' && typeof row.page_source_id === 'string' && row.page_source_id.trim() !== '')
        page.pageSourceId = row.page_source_id.trim();
      return page;
    });

/** The site's own language (`mx`), the one its link texts and section titles are meant to be read in; '' when the
 *  service names none. */
export const parseSiteLanguage = (body: Record<string, unknown>): string =>
  typeof body.language === 'string' ? body.language.trim() : '';

/** Whether the service read every navigation the site renders, so the pages' `linked` can decide which of them the site
 *  offers. False when a read failed, and from a service that does not say: the site's own navigation decides then. */
export const parseLinkedKnown = (body: Record<string, unknown>): boolean => body.linked_known === true;

/** Whether the service answered without something it could not read (owner-hub's configurator, a navigation source, the
 *  source of a list or page a link names, the page under a list page's list, whose `pageSourceId` is then absent, or
 *  the request ending while they were read): still an answer, kept briefly (`SITE_PAGES_DEGRADED_CACHE_MS`), as the
 *  service keeps it. True as well from a service that does not say: one from before `degraded` answers the same way
 *  without it, and keeps such an answer briefly itself. */
export const parseDegraded = (body: Record<string, unknown>): boolean => body.degraded !== false;

/** The service's answer: the site's pages, the site's own language, whether the pages' `linked` counts, and whether it
 *  may lack something the service could not read. */
export interface SitePagesAnswer {
  pages: SitePage[];
  language: string;
  linkedKnown: boolean;
  degraded: boolean;
}

const parseSitePagesAnswer = (body: Record<string, unknown>): SitePagesAnswer => ({
  pages: parseSitePages(body),
  language: parseSiteLanguage(body),
  linkedKnown: parseLinkedKnown(body),
  degraded: parseDegraded(body),
});

/** The pages of the brand's site that show game lists, the home page first, and the site's own language. `domain` is
 *  the site's host. */
export const getSitePages = async (
  casinoInstanceId: string,
  domain: string,
  signal?: AbortSignal,
): Promise<TenantSourceResult<SitePagesAnswer>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const response = await axios.get(
      `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/site-pages?domain=${encodeURIComponent(domain)}`,
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parseSitePagesAnswer);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

/** How long an answer stands for every reader of the brand's site. The pages follow the platform's config, which no
 *  owner action changes; their `lists` follow the brand's own writes too (a page's arrangement published or reverted,
 *  one of its own lists deleted), and each of those drops the brand's answers (`dropSitePages`). */
export const SITE_PAGES_CACHE_MS = 300_000;

/** How long a degraded answer stands (see `parseDegraded`): the service keeps one this long too, so the next reader after
 *  it asks for a whole answer. */
export const SITE_PAGES_DEGRADED_CACHE_MS = 30_000;

// The answers that came in, and the requests in flight, per brand and host: every reader shares one. A drop bumps the
// generation of the keys it forgets, and an answer is kept only under the generation its request went out in.
const answers = new Map<string, { answer: SitePagesAnswer; at: number }>();
const asking = new Map<string, Promise<TenantSourceResult<SitePagesAnswer>>>();
const generations = new Map<string, number>();
const generationOf = (key: string): number => generations.get(key) ?? 0;

const answerKey = (casinoInstanceId: string, domain: string): string => `${casinoInstanceId}|${domain}`;

/** The answer for the brand and host if one younger than `SITE_PAGES_CACHE_MS` is in (`SITE_PAGES_DEGRADED_CACHE_MS` for
 *  a degraded one), else undefined: a reader can open with it at its first render. */
export const peekSitePages = (casinoInstanceId: string, domain: string): SitePagesAnswer | undefined => {
  const kept = answers.get(answerKey(casinoInstanceId, domain));
  if (!kept) return undefined;
  const keptFor = kept.answer.degraded ? SITE_PAGES_DEGRADED_CACHE_MS : SITE_PAGES_CACHE_MS;
  return Date.now() - kept.at < keptFor ? kept.answer : undefined;
};

/**
 * `getSitePages`, shared by every reader of the brand's site: the fresh answer, else the request in flight, else one new
 * request. It carries no reader's signal, so one reader leaving never cancels it for another; a reader checks its own
 * abort once it has the answer. Only an answer that came in is kept: after a failed read, the next reader asks again. An
 * answer that comes in after a drop of the brand (`dropSitePages`) answers its readers and is not kept.
 */
export const getSitePagesShared = (
  casinoInstanceId: string,
  domain: string,
): Promise<TenantSourceResult<SitePagesAnswer>> => {
  const fresh = peekSitePages(casinoInstanceId, domain);
  if (fresh) return Promise.resolve<TenantSourceResult<SitePagesAnswer>>({ ok: true, value: fresh });
  const key = answerKey(casinoInstanceId, domain);
  const inFlight = asking.get(key);
  if (inFlight) return inFlight;
  const generation = generationOf(key);
  const request = getSitePages(casinoInstanceId, domain)
    .then((result) => {
      if (result.ok && generationOf(key) === generation) answers.set(key, { answer: result.value, at: Date.now() });
      return result;
    })
    .finally(() => {
      // After a drop the key may belong to a newer request: only this request's own entry is cleared.
      if (asking.get(key) === request) asking.delete(key);
    });
  asking.set(key, request);
  return request;
};

/** Forget the brand's answers for every host, and its requests in flight, after a write that changes its pages' lists:
 *  the next reader asks again. A request in flight now still answers its readers, but is not kept, and a reader after
 *  the drop does not join it. */
export const dropSitePages = (casinoInstanceId: string): void => {
  const brand = answerKey(casinoInstanceId, '');
  for (const key of new Set([...answers.keys(), ...asking.keys()])) {
    if (!key.startsWith(brand)) continue;
    answers.delete(key);
    asking.delete(key);
    generations.set(key, generationOf(key) + 1);
  }
};
